import { tr } from '@/lib/i18n-core';
import { useQuery } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { money } from '@/lib/utils';
import { Badge, Field, Input, Select } from '@/components/ui/primitives';
import { durationLabel } from './calendar-time';

export interface BookableService {
  id: string;
  code: string;
  name: string;
  basePriceMinor: number;
  durationMinutes?: number | null;
}

export interface ServiceChoice {
  chargeItemId: string;
  durationMinutes: number;
}

export const DEFAULT_SERVICE_MINUTES = 15;

export function useBookableServices(departmentId: string | null | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ['bookable-services', departmentId ?? ''],
    queryFn: async () =>
      (await get<{ data: BookableService[] }>(`/charge-items${qs({ departmentId: departmentId || undefined, bookable: 1, limit: 200 })}`)).data,
    enabled,
    staleTime: 300_000,
  });
}

export function serviceMinutes(service: BookableService | undefined): number {
  return service?.durationMinutes && service.durationMinutes > 0 ? service.durationMinutes : 0;
}

export function ServicePicker({
  departmentId,
  value,
  onChange,
  disabled,
}: {
  departmentId: string | null | undefined;
  value: ServiceChoice | null;
  onChange: (next: ServiceChoice | null) => void;
  disabled?: boolean;
}) {
  const services = useBookableServices(departmentId, !disabled);
  const chosen = services.data?.find((service) => service.id === value?.chargeItemId);
  const fixedMinutes = serviceMinutes(chosen);
  const asksForLength = Boolean(chosen) && fixedMinutes === 0;

  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
      <Field label={tr('opd', 'service')} hint={services.data && services.data.length === 0 ? tr('opd', 'no_bookable_services_for_this_department') : undefined}>
        <div className="flex items-center gap-2">
          <Select
            value={value?.chargeItemId ?? ''}
            disabled={disabled || services.isLoading}
            onChange={(event) => {
              const next = services.data?.find((service) => service.id === event.target.value);
              if (!next) {
                onChange(null);
                return;
              }
              const minutes = serviceMinutes(next);
              onChange({ chargeItemId: next.id, durationMinutes: minutes > 0 ? minutes : DEFAULT_SERVICE_MINUTES });
            }}
          >
            <option value="">{tr('opd', 'consultation_no_service')}</option>
            {services.data?.map((service) => (
              <option key={service.id} value={service.id}>
                {service.name} · {money(service.basePriceMinor)}
                {serviceMinutes(service) > 0 ? ` · ${durationLabel(serviceMinutes(service))}` : ''}
              </option>
            ))}
          </Select>
          {chosen && fixedMinutes > 0 && <Badge variant="secondary">{durationLabel(fixedMinutes)}</Badge>}
        </div>
      </Field>
      {asksForLength && (
        <Field label={tr('opd', 'length_minutes')} required>
          <Input
            type="number"
            min={5}
            max={1440}
            step={5}
            className="w-28"
            value={value?.durationMinutes ?? DEFAULT_SERVICE_MINUTES}
            onChange={(event) => {
              const minutes = Number(event.target.value);
              if (value) onChange({ ...value, durationMinutes: Number.isFinite(minutes) ? minutes : DEFAULT_SERVICE_MINUTES });
            }}
          />
        </Field>
      )}
    </div>
  );
}
